export type PaymentPlan =
  | 'BUSINESS_WATERMARK'
  | 'FILMR_PRO'
  | 'FREE'
  | 'PROFESSIONAL'
  | 'BUSINESS_TRIAL'
  | 'BUSINESS'
  | 'UNLIMITED'
  | 'PREMIUM';

interface PlanPrice {
  monthly: {
    currency: string;
    price: number;
    subText: string;
    starText?: string;
  };
  annually: {
    currency: string;
    price: number;
    subText: string;
    starText?: string;
  };
}
interface CTA {
  monthly: {
    text: string;
    url: string;
    action?: string;
    backgroundColorClass: string;
    textColorClass: string;
  };
  annually: {
    text: string;
    url: string;
    action?: string;
    backgroundColorClass: string;
    textColorClass: string;
  };
}

interface Feature {
  highlightedText: string;
  plainText: string;
}
interface Redirection {
  url: string;
  type: string;
  modal?: boolean;
}
interface AppPromo {
  firstLine: string;
  secondLine: string;
  CTAText: string;
  backgroundGradientClass: string;
  appImage: string;
}

export interface PlanAction {
  actionText: string;
  action: string;
  actionUrl: string;
  displayText: string;
}

export interface Stripe {
  gatewayPlanId: string;
  inrPlanId: string;
  usdPlanId: string;
  productId: string;
}

export interface Recurly {
  gatewayPlanId: string;
}

export interface PlanAction {
  actionText: string;
  action: string;
  actionUrl: string;
  displayText: string;
}

export interface GatewayData {
  gatewayPlanId?: string;
  inrPlanId?: string;
  usdPlanId?: string;
  productId?: string;
}

export interface DurationObject {
  durationInDays: number;
  durationDisplay: string;
  planAction?: PlanAction;
  basePrice: number;
  displayPrice?: number | string;
  displayBasePrice?: number | string;
  sellingPrice: number;
  stripe: GatewayData;
  recurly: GatewayData;
  paddle?: GatewayData;
  product_code: string;
  monthly_price: number;
  monthlyPrice?: number;
  monthly_price_display: number;
}

export interface Plan {
  name: string;
  description: string;
  bannerText?: string;
  planPrice?: PlanPrice;
  CTA: CTA;
  feature: Feature[];
  features: string[];
  key_features: string[];
  currency_symbol: string;
  label: string;
  rank: number;
  tagline: string;
  featuresCheckImage: string;
  redirection: Redirection;
  appPromo?: AppPromo;
  durations: DurationObject[];
  id: number;
}

export type PricingPlans = Plan[];

export interface PlanResponse {
  id: number;
  rank: number;
  name: string;
  label: string;
  description: string;
  tagline: string;
  currency_symbol: string;
  features: Array<string>;
  key_features?: Array<string>;
  durations: Array<DurationObject>;
  color?: string;
  appsumo?: boolean;
  gateway?: GatewayData;
  duration?: Array<DurationObject>;
}

interface AiPlanDescription {
  desktop: string;
  mobile: string;
}

interface AiPlanPrice {
  monthly: {
    currency: string;
    price: number;
    planId: string;
    subText?: string;
  };
  yearly: {
    currency: string;
    price: number;
    planId: string;
    subText?: string;
  };
}

interface AiCTA {
  text: string;
  url: string;
  action?: string;
  backgroundColorClass: string;
  textColorClass: string;
}

export interface AiPlan {
  key: string;
  name: string;
  subText?: string;
  description: AiPlanDescription;
  bannerText?: string;
  planPrice: AiPlanPrice;
  CTA: AiCTA;
  key_features: UsageFeature[];
  currency_symbol: string;
  redirection: Redirection;
  sequence: number;
  usageQuota: UsageQuota;
  supported_item_plans?: string[];
}

export enum PricingDuration {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export type PricingProps = {
  currency: string;
  price: number;
  subText: string;
  starText?: string;
};

export type PricingRedirectionProps = {
  type: string;
  url: string;
  modal?: boolean;
};

export type PricingPlanTopProps = {
  planName: string;
  planSubText?: string;
  planKey: string;
  planPrice: AiPlanPrice;
  bannerText?: string;
  isYearlyDuration: boolean;
  CTA: {
    text: string;
    backgroundColorClass: string;
  };
  theme?: 'dark' | 'light';
  onClick: () => void;
  t?: Function;
  disabled?: boolean;
  classNames?: string;
  renderPropsTop?: () => JSX.Element;
  usageQuota: UsageQuota;
  addonPlan?: AddonSubscriptionPlan;
};

export type PricingPlanBottomProps = {
  planKey: string;
  usageQuota: UsageQuota;
  goToPlanDetails?: Function;
  CTA: {
    text: string;
    backgroundColorClass: string;
  };
  theme?: 'dark' | 'light';
  onClick: () => void;
  isYearlyDuration: boolean;
  addonPlan?: AddonSubscriptionPlan;
  ladder?: PublicUILadder;
};

export type PlanProps = PricingPlanTopProps & PricingPlanBottomProps & {
  ladder?: PublicUILadder;
  duration?: PricingDuration;
  onBoostChange?: (type: 'increment' | 'decrement') => void;
  onCreditChange?: (type: 'increment' | 'decrement') => void;
};

export interface UsageQuota {
  premium_minutes?: number;
  premium_media?: number;
  exports?: number;
  storage?: number;
  ai_credits?: number;
  voices?: number;
  generation_minutes?: number;
  ai_credits_v2?: number;
  credits_v2?: number;
}

export interface UsageFeature {
  description: string;
  id: string;
  top_feature: boolean;
  type: keyof UsageQuota;
  tags: string[];
}

export interface BillingDetails {
  amount: number;
  currency: string;
  symbol: string;
  billing_cycle: string | null;
  plan_id: string;
  gated_features: string[];
}

export interface BasePlanStructure {
  sequence: number;
  stores: string[];
  usage_quota: UsageQuota;
  usage_features: UsageFeature[];
  deprecated: boolean;
  active: boolean;
}

export interface RegularPlan extends BasePlanStructure {
  monthly: BillingDetails;
  yearly: BillingDetails;
  supported_item_plans?: string[];
}

export interface AddonPlan extends BasePlanStructure {
  once: BillingDetails;
}

export interface AddonSubscriptionPlan extends BasePlanStructure {
  id: AddonSubscriptionPlanKey;
  monthly: BillingDetails;
  yearly: BillingDetails;
}

export interface PricingV2APIResponse {
  plans: {
    [key: string]: RegularPlan;
  };
  addon_plans: {
    [key: string]: AddonPlan;
  };
  addon_subscription_plans: {
    [key: string]: AddonSubscriptionPlan;
  };
}

// New types for v40July ladder-based pricing (adapted for public pages)

export type PlanKey = "free" | "plus" | "max" | "generative" | "team";
export type AddonPlanKey =
  | "basic"
  | "essential"
  | "generative"
  | "stock_pack"
  | "ads_pack"
  | "videos_pack"
  | "large_ads_pack"
  | "large_videos_pack";
export type AddonSubscriptionPlanKey =
  | "generative"
  | "generative_v2_pack"
  | "stock_v2_pack"
  | "generative_mega_pack";

export interface Price {
  planId: string;
  amount: number;
  symbol: string;
}

export interface UIFeature {
  id: string;
  type: string;
  description: string;
}

export interface PublicUILadder {
  index: number;
  addon: AddonSubscriptionPlan | null;
  boostHasAddons: number;
  addOnMultiplier: number;
  boostMultiplier: number;
  prevBoost: boolean;
  nextBoost: boolean;
  prevCredit: boolean;
  nextCredit: boolean;
  totalMonthlyPrice: number;
  totalYearlyPrice: number;
  yearlyCostPerCredit: number;
  monthlyCostPerCredit: number;
  credits: number;
  uiFeatures: UIFeature[];
  addonDescription: string;
  addOnFeatures: string[];
  boostFeatures: string[];
  asteriskText: {
    monthly: string | null;
    yearly: string | null;
  };
  ctaText: "Get Started" | "Start Free Trial" | "Contact Sales";
}

export interface PublicUIPlan {
  id: PlanKey;
  name: string;
  deprecated: boolean;
  tags: Array<string>;
  description: string;
  mobileDescription: string;
  monthly: Price;
  yearly: Price;
  supported_item_plans: string[];
  additionalFeatures: Array<UsageFeature>;
  usageQuota: UsageQuota;
  isFreePlan: boolean;
  subtitle: string;
  features: Array<UsageFeature>;
  cursor: number;
  ladders: PublicUILadder[];
}

export interface PublicPricingState {
  selectedDuration: PricingDuration;
  plans: PublicUIPlan[];
  loading: boolean;
  error: string | null;
}
